import type { HistoryItem } from '@/lib/api';
import { groupHistoryByDate, historyEntryLabel } from '@/lib/history';
import { IressIcon, IressText, IressStack } from '@iress-oss/ids-components';
import { PageError } from './PageError';
import './HistoryList.css';

interface HistoryListProps {
  items: HistoryItem[];
  loading: boolean;
  error: string | null;
  onSelect: (queryId: string) => void;
  onRefresh: () => void;
}

/**
 * The History timeline: date groups newest → oldest, flat newest → oldest
 * within each group. Renders the empty state when there are no items and a
 * list-level error with Refresh on failure. There is no load-more / pagination
 * / scroll-to-load affordance (the backend caps the list at 10).
 */
export function HistoryList({
  items,
  loading,
  error,
  onSelect,
  onRefresh,
}: HistoryListProps) {
  if (loading) {
    return (
      <div className="porter-history" aria-busy="true">
        <IressText element="p" className="porter-history__loading">
          Loading your recent queries…
        </IressText>
      </div>
    );
  }

  if (error) {
    return <PageError onRetry={onRefresh} />;
  }

  if (items.length === 0) {
    return (
      <div className="porter-history">
        <IressText element="p" className="porter-history__empty">
          Your recent queries will appear here.
        </IressText>
      </div>
    );
  }

  const groups = groupHistoryByDate(items);

  return (
    <IressStack gap="spacing.3" className="porter-history">
      {groups.map((group) => (
        <section key={group.dateKey} className="porter-history__group">
          <IressText element="h2" className="porter-history__date">
            {group.dateLabel}
          </IressText>
          <ul className="porter-history__list">
            {group.items.map((item) => (
              <li key={item.queryId}>
                <button
                  className="porter-history__entry"
                  onClick={() => onSelect(item.queryId)}
                  type="button"
                >
                  <span className="porter-history__entry-label">
                    {historyEntryLabel(item)}
                  </span>
                  <span className="porter-history__chevron">
                    <IressIcon name="chevron_right" />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <IressText element="p" color="muted" className="porter-history__note">
        Showing up to 10 recent queries.
      </IressText>
    </IressStack>
  );
}
